import { useCallback, useState } from "react";

import { format } from "@cloudinary/url-gen/actions/delivery";
import { scale } from "@cloudinary/url-gen/actions/resize";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import CheckCircleIcon from "@heroicons/react/24/solid/CheckCircleIcon";
import ExclamationTriangleIcon from "@heroicons/react/24/solid/ExclamationTriangleIcon";
import Cropper from "react-easy-crop";
import { Point, Area } from "react-easy-crop/types";

import { useUploadPhotosContext } from "components/form/uploadPhotos/UploadPhotos";
import { useGetProduct } from "queries/product-queries";
import { UploadedPhoto } from "types/schema";
import { cloudinary } from "utils/cloudinary-utils";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import { calculateAspectFromRatio } from "utils/schema-utils";

import styles from "./PhotoCropper.module.scss";

type PhotoCropperProps = {
  cropData: UploadedPhoto["crop"];
  onCrop: (c: UploadedPhoto["crop"]) => void;
};

const CROPPER_IMAGE_WIDTH = 720;

export function getResizedCropperImage(photoId: string) {
  const cloudinaryImage = cloudinary()
    .image(photoId)
    .resize(scale().width(CROPPER_IMAGE_WIDTH))
    .delivery(format(auto()));

  return cloudinaryImage;
}

/**
 * PhotoCropper takes an uploaded photo and renders it in a `react-easy-crop` component.
 */
export default function PhotoCropper({ cropData, onCrop }: PhotoCropperProps) {
  const interpolate = useInterpolate();

  const { data: product } = useGetProduct();
  const { aspectRatio, name, photo } = useUploadPhotosContext();

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!photo) {
    throw new Error("Photo cropper called without photo");
  }

  const photoScale = photo.width / CROPPER_IMAGE_WIDTH;

  /**
   * If, after cropping, the image will be less than 800px in either dimension,
   * display a warning that it might not print well.
   *
   * Otherwise, let the user know it's a good'un.
   */
  let photoStatus: "default" | "valid" | "invalid" = "default";

  if (cropData) {
    if (cropData.width > 800 && cropData.height > 800) {
      photoStatus = "valid";
    } else {
      photoStatus = "invalid";
    }
  }

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      const { height, width, x, y } = croppedAreaPixels;
      onCrop({
        height: Math.round(height * photoScale),
        width: Math.round(width * photoScale),
        x: Math.round(x * photoScale),
        y: Math.round(y * photoScale),
      });
    },
    [onCrop, photoScale]
  );

  return (
    <>
      <div className={styles.cropOuter}>
        <div
          className={styles.cropContainer}
          data-testid={`photo_cropper_${name}`}
        >
          <Cropper
            aspect={calculateAspectFromRatio(aspectRatio)}
            crop={crop}
            image={getResizedCropperImage(photo.public_id).toURL()}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            restrictPosition
            zoom={zoom}
            // Load previous crop
            initialCroppedAreaPixels={{
              height: photo.crop.height / photoScale,
              width: photo.crop.width / photoScale,
              x: photo.crop.x / photoScale,
              y: photo.crop.y / photoScale,
            }}
          />
        </div>
      </div>

      {photoStatus === "valid" && (
        <div className={styles.valid}>
          <CheckCircleIcon className={styles.statusIcon} />

          <p className={styles.statusMessage}>
            {interpolate(
              product.content.canvas.form_upload_photo_valid ||
                CANVAS_FALLBACK_CONTENT.form_upload_photo_valid
            )}
          </p>
        </div>
      )}

      {photoStatus === "invalid" && (
        <div className={styles.invalid}>
          <ExclamationTriangleIcon className={styles.statusIcon} />

          <p className={styles.statusMessage}>
            {interpolate(
              product.content.canvas.form_upload_photo_invalid ||
                CANVAS_FALLBACK_CONTENT.form_upload_photo_invalid
            )}
          </p>
        </div>
      )}
    </>
  );
}
